import React from 'react';
import { SchoolListing } from '../types/api';
import { CheckIcon, StarIcon } from 'lucide-react';
import { parseBulletPoints } from '../utils/htmlParser';
interface SchoolCardProps {
  school: SchoolListing;
  rank: number;
  isRecommended?: boolean;
}
export function SchoolCard({
  school,
  rank,
  isRecommended = false
}: SchoolCardProps) {
  const bullets = parseBulletPoints(school.bodyContent);
  return <a href={school.clickURL} target="_blank" rel="noopener noreferrer" className={`block bg-white rounded-xl overflow-hidden hover:shadow-md transition-all relative group cursor-pointer ${isRecommended ? 'shadow-lg border-2 border-blue-400 bg-gradient-to-br from-blue-50/30 to-indigo-50/20' : 'shadow-sm border border-gray-200'}`} data-id="element-441" data-mp="2v5wfh926jvrf">
      {/* Recommended Banner */}
      {isRecommended && <div className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center py-2 px-4 font-bold text-sm flex items-center justify-center gap-2" data-id="element-442" data-mp="ke4vw3hr770u">
          <span className="text-lg" data-id="element-443" data-mp="fhw9q6552dk6">🏆</span>
          <span data-id="element-444" data-mp="3597ys36miwmr">#1 Recommended School</span>
        </div>}

      {/* Top Highlight Badge */}
      {school.schoolHighlight && <div className={`absolute ${isRecommended ? 'top-10' : 'top-0'} left-0 bg-[#1a2332] text-white text-xs font-bold px-4 py-1.5 rounded-br-lg z-10 flex items-center gap-1.5`} data-id="element-445" data-mp="3a7szs4tp9kc9">
          <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-446" data-mp="3j90wiigkid48" />
          {school.schoolHighlight}
        </div>}

      <div className={`p-4 md:p-6 ${isRecommended && school.schoolHighlight ? 'pt-12 md:pt-14' : school.schoolHighlight ? 'pt-12 md:pt-14' : isRecommended ? 'pt-2' : ''}`} data-id="element-447" data-mp="2ml2zp90wvzua">
        {/* ===== MOBILE LAYOUT ===== */}
        <div className="md:hidden" data-id="element-448" data-mp="2viwrc6nqe5b4">
          {/* Row 1: Rank + Logo + CTA */}
          <div className="flex items-center gap-3 mb-3" data-id="element-449" data-mp="30c80wfjxh06q">
            <div className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 font-medium text-xs flex-shrink-0" data-id="element-450" data-mp="23ta9tnh52uqs">
              {rank}
            </div>
            <div className="w-32 h-20 flex items-center justify-center bg-white rounded border border-gray-100 p-2 flex-shrink-0" data-id="element-451" data-mp="3b5t47pq2y756">
              <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-452" data-mp="367ht7bo1tqld" />
            </div>
            <div className="ml-auto bg-[#e8594f] group-hover:bg-[#d64a41] text-white font-bold py-2.5 px-4 rounded-lg text-center transition-colors shadow-sm text-sm whitespace-nowrap" data-id="element-453" data-mp="3kxp2p1w2vz9x">
              View Programs
            </div>
          </div>

          {/* Row 2: School name + headContent */}
          <h2 className="text-base font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors mb-0.5 leading-snug" data-id="element-454" data-mp="2n9yhu1e4bvvs">
            {school.mBrandName}
          </h2>

          {school.headContent && <p className="text-sm text-gray-600 mb-3 leading-snug" data-id="element-455" data-mp="2x4qi5cz34wu9">
              {school.headContent}
            </p>}

          {/* Bullets */}
          <ul className="space-y-2 mb-2" data-id="element-456" data-mp="cgc6axk06erm">
            {bullets.slice(0, 3).map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed" data-id="element-457" data-mp="jzleingrw7f3" data-mpi={idx}>
                <CheckIcon className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-458" data-mp="oy53kwqnxzbo" />
                <span data-id="element-459" data-mp="a15m8h60es40">{bullet}</span>
              </li>)}
          </ul>

          <div className="flex items-center gap-1.5 text-xs font-bold text-[#22c55e]" data-id="element-460" data-mp="a2yug0w9tke4">
            <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-461" data-mp="f0qy1s6a2uy5" />
            100% Online
          </div>
        </div>

        {/* ===== DESKTOP LAYOUT ===== */}
        <div className="hidden md:flex gap-6" data-id="element-462" data-mp="17omowg961sn0">
          {/* Left: Rank */}
          <div className="flex items-start pt-1 flex-shrink-0" data-id="element-463" data-mp="17qqssy72erna">
            <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 font-medium text-sm flex-shrink-0" data-id="element-464" data-mp="szgud5l9wqlg">
              {rank}
            </div>
          </div>

          {/* Center: Content */}
          <div className="flex-grow min-w-0" data-id="element-465" data-mp="1qfdxhflbxf0c">
            <h2 className="text-lg font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors mb-0.5 leading-snug" data-id="element-466" data-mp="1qg5sopb0laq3">
              {school.mBrandName}
            </h2>

            {school.headContent && <p className="text-sm text-gray-600 mb-3 leading-snug" data-id="element-467" data-mp="3twkkij578dze">
                {school.headContent}
              </p>}

            <ul className="space-y-2 mb-1" data-id="element-468" data-mp="3p0daof9omic6">
              {bullets.slice(0, 4).map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed" data-id="element-469" data-mp="3k10o7vmq3da4" data-mpi={idx}>
                  <CheckIcon className="w-4 h-4 text-[#22c55e] flex-shrink-0 mt-0.5" data-id="element-470" data-mp="lgsdlq0kzm83" />
                  <span data-id="element-471" data-mp="giz9r3mr34az">{bullet}</span>
                </li>)}
            </ul>
          </div>

          {/* Right: Logo + CTA */}
          <div className="flex flex-col items-center w-44 flex-shrink-0 border-l border-gray-100 pl-5" data-id="element-472" data-mp="bpe913trvw83">
            <div className="w-36 h-20 flex items-center justify-center bg-white rounded-lg border border-gray-100 p-2 group-hover:border-gray-300 transition-colors mb-3" data-id="element-473" data-mp="6qtgzroociuq">
              <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-474" data-mp="zg0h2v3s1gi3" />
            </div>

            <div className="w-full bg-[#e8594f] group-hover:bg-[#d64a41] text-white font-bold py-3 px-4 rounded-lg text-center transition-colors shadow-sm text-sm" data-id="element-475" data-mp="1braxnvulgfqb">
              View Programs
            </div>

            <div className="mt-3 flex items-center gap-1.5 text-sm font-bold text-[#22c55e]" data-id="element-476" data-mp="1lq0jfzpm2kbu">
              <StarIcon className="w-4 h-4 fill-current" data-id="element-477" data-mp="1qpd5wjcklpdw" />
              100% Online
            </div>
          </div>
        </div>
      </div>
    </a>;
}